import React, { useEffect, useRef, useState } from "react";
import collapseBlueIcon from "@src/assets/svg/collapse-blue.svg";
import expandBlueIcon from "@src/assets/svg/expand-blue.svg";
import renameBlueIcon from "@src/assets/svg/rename-blue-icon.svg";
import Spinner from "@src/components/atoms/spinner/spinner";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";
import {
  CrossNameSpaceOrganziation,
  MachineGroup,
} from "@src/types/namespace-configuration";
import { useMachineGroupsListAdminPanelHook } from "@src/components/organisms/machine-groups-list-admin-panel/use-machine-groups-list-admin-panel-hook";
import MachineGroupExpandedView from "@src/components/organisms/machine-group-expanded-view/machine-group-expanded-view";
import styles from "./machine-groups-list-admin-panel.module.scss";

const cssClassName = "machine-groups-list-admin-panel";

type MachineGroupsListAdminPanelProps = Omit<
  ReturnType<typeof useMachineGroupsListAdminPanelHook>,
  "refreshMachineGroups"
> & {
  organizations: CrossNameSpaceOrganziation[];
  createdMachineGroupId?: string;
  onCreatedMachineGroupExpanded: (machineGroupId: string) => void;
  onEditMachineGroup: (machineGroup: MachineGroup) => void;
  onRoutingPathSaved: () => void;
};

const MachineGroupsListAdminPanel = (
  props: MachineGroupsListAdminPanelProps,
) => {
  const {
    createdMachineGroupId,
    machineGroups,
    organizations,
    permissions,
    isLoading,
    texts,
    onCreatedMachineGroupExpanded,
    onEditMachineGroup,
    onRoutingPathSaved,
  } = props;

  const [expandedMachineGroupId, setExpandedMachineGroupId] = useState<
    string | null
  >(null);
  const listRef = useRef<HTMLDivElement>(null);
  const expandedGroupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading || !createdMachineGroupId) return;

    if (machineGroups.some((group) => group.id === createdMachineGroupId)) {
      setExpandedMachineGroupId(createdMachineGroupId);
      onCreatedMachineGroupExpanded(createdMachineGroupId);
    }
  }, [
    createdMachineGroupId,
    isLoading,
    machineGroups,
    onCreatedMachineGroupExpanded,
  ]);

  useEffect(() => {
    const list = listRef.current;
    const expandedGroup = expandedGroupRef.current;

    if (!expandedMachineGroupId || !list || !expandedGroup) return;

    const listBounds = list.getBoundingClientRect();
    const groupBounds = expandedGroup.getBoundingClientRect();
    const visibleListTop = listBounds.top + list.clientTop;
    const centeredScrollTop =
      list.scrollTop +
      groupBounds.top -
      visibleListTop -
      (list.clientHeight - groupBounds.height) / 2;

    list.scrollTop = Math.max(0, centeredScrollTop);
  }, [expandedMachineGroupId, isLoading, machineGroups]);

  return (
    <div aria-busy={isLoading} className={styles[cssClassName]}>
      {!isLoading && machineGroups.length === 0 && (
        <div className={styles[`${cssClassName}__empty-state`]}>
          {texts.noMachineGroups}
        </div>
      )}
      {!isLoading && machineGroups.length > 0 && (
        <div className={styles[`${cssClassName}__list`]} ref={listRef}>
          {machineGroups.map((machineGroup) => {
            const isExpanded = expandedMachineGroupId === machineGroup.id;

            return (
              <div
                className={[
                  styles[`${cssClassName}__group`],
                  isExpanded && styles[`${cssClassName}__group--expanded`],
                ]
                  .filter(Boolean)
                  .join(" ")}
                key={machineGroup.id}
                ref={isExpanded ? expandedGroupRef : undefined}
              >
                <div
                  className={[
                    styles[`${cssClassName}__group-header`],
                    isExpanded &&
                      styles[`${cssClassName}__group-header--expanded`],
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <button
                    aria-expanded={isExpanded}
                    aria-label={`${
                      isExpanded ? texts.collapseGroup : texts.expandGroup
                    } ${machineGroup.name}`}
                    className={styles[`${cssClassName}__expand-button`]}
                    onClick={() =>
                      setExpandedMachineGroupId(
                        isExpanded ? null : machineGroup.id,
                      )
                    }
                    type="button"
                  >
                    <img
                      alt=""
                      aria-hidden="true"
                      className={styles[`${cssClassName}__expand-icon`]}
                      src={isExpanded ? collapseBlueIcon : expandBlueIcon}
                    />
                  </button>
                  <div className={styles[`${cssClassName}__group-details`]}>
                    <span className={styles[`${cssClassName}__group-name`]}>
                      {machineGroup.name}
                    </span>
                    <span
                      aria-label={`${machineGroup.sources.length} ${texts.sources}`}
                      className={styles[`${cssClassName}__source-count`]}
                    >
                      {machineGroup.sources.length}
                    </span>
                  </div>
                  {isExpanded && (
                    <RenderIfUserHasPermission permission="manage-machine-groups">
                      <button
                        aria-label={`${texts.editGroup} ${machineGroup.name}`}
                        className={styles[`${cssClassName}__rename-action`]}
                        onClick={() => onEditMachineGroup(machineGroup)}
                        type="button"
                      >
                        <img alt="" aria-hidden="true" src={renameBlueIcon} />
                        <span>{texts.editGroup}</span>
                      </button>
                    </RenderIfUserHasPermission>
                  )}
                </div>
                {isExpanded && (
                  <MachineGroupExpandedView
                    machineGroup={machineGroup}
                    onRoutingPathSaved={onRoutingPathSaved}
                    permissions={permissions}
                    organizations={organizations}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      {isLoading && (
        <div className={styles[`${cssClassName}__loader-wrapper`]}>
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default MachineGroupsListAdminPanel;
